import React from "react";
import {
  Alert,
  Collapse,
  Descriptions,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { Link } from "react-router-dom";
import routes from "../../../../engine/routes";
import { RecordsTable } from "./RecordsTable";

const { Text } = Typography;

const plural = (n, one, many) => `${n} ${n > 1 ? many : one}`;

/** The action(s) the records of one section receive — summarised in its header, detailed per row in the Action column. */
const actionSummary = (r, hasDuplicates) => {
  if (r.decision === "blacklists") return "actions : garder / retirer";
  if (r.group === "addresses" || r.group === "responsables")
    return "actions : garder / fusionner / modifier";
  if (r.key === "users.institution" && hasDuplicates)
    return "actions : rattacher / fusionner / renommer";
  return "action : rattacher";
};

/**
 * Step 5 — the preview. Not a table of numbers: every record the merge will touch is
 * listed with its value before and after, one section per registry key (all open),
 * then the address ownership matrix, cross-billing and invoice collisions.
 * The totals in the section headers are exactly what the post-merge verification checks.
 */
export function StepPreview({ preview, merge }) {
  const sources = preview.sources;
  const mergeId = merge && merge._id;
  const targetName =
    (merge && merge.decisions && merge.decisions.targetName) ||
    (preview.existingTarget && preview.existingTarget.name) ||
    "la nouvelle institution";
  const hasDuplicates = !!(
    preview.users &&
    preview.users.duplicates &&
    preview.users.duplicates.length
  );

  const sections = [
    {
      key: "institutions",
      label: "Institutions sources",
      model: "Institution",
      total: sources.length,
      header: (
        <Space wrap size={[4, 4]}>
          <Text strong>
            Institutions sources —{" "}
            {plural(sources.length, "archivée", "archivées")}
          </Text>
          <Text type="secondary">→ {targetName}</Text>
          <Text type="secondary">— action : archiver</Text>
          {sources.map(s => (
            <Tag key={s._id}>{s.name}</Tag>
          ))}
        </Space>
      ),
    },
    ...preview.relations
      .filter(r => r.total > 0)
      .map(r => ({
        ...r,
        header: (
          <Space wrap size={[4, 4]}>
            <Text strong>
              {r.label} — {plural(r.total, "enregistrement", "enregistrements")}
            </Text>
            <Text type="secondary">— {actionSummary(r, hasDuplicates)}</Text>
            {r.decision && <Tag color="purple">décision</Tag>}
            {sources
              .filter(s => r.perSource[s._id] > 0)
              .map(s => (
                <Tag key={s._id}>
                  {s.name} {r.perSource[s._id]}
                </Tag>
              ))}
          </Space>
        ),
      })),
  ];

  const addr = preview.addresses;
  const addrColumns = [
    {
      title: "Adresse",
      render: (_, a) => (
        <span>
          {a.name && <Text strong>{a.name} </Text>}
          {a.street || a.city
            ? `${a.street} ${a.city}`.trim()
            : // map places (type « maps ») carry no street — show the place text instead
              a.location || (a.name ? "" : "(lieu sans libellé)")}
        </span>
      ),
    },
    {
      title: "Propriétaire",
      render: (_, a) => (
        <Space>
          {a.owner.name}
          <Tag color={a.owner.isSource ? "blue" : "default"}>
            {a.owner.isSource ? "fusionné" : "hors fusion"}
          </Tag>
        </Space>
      ),
    },
    {
      title: "Partagée vers",
      dataIndex: "sharedTo",
      render: xs =>
        xs.length ? (
          xs.map(x => (
            <Tag key={x._id} color={x.isSource ? "blue" : "default"}>
              {x.name}
            </Tag>
          ))
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    { title: "Factures", dataIndex: "bills", align: "right" },
    { title: "Prestations", dataIndex: "prestations", align: "right" },
    {
      title: "Résultat",
      dataIndex: "verdict",
      render: v =>
        v === "ownership_moves" ? (
          <Tag color="green">la propriété passe à la nouvelle institution</Tag>
        ) : (
          <Tag color="orange">
            reste à son propriétaire — seul l'usage est repointé
          </Tag>
        ),
    },
  ];

  const xb = preview.crossBilling;
  const xbOutside = xb.clusterBooksOutsidePays + xb.outsideBooksClusterPays;
  const xbColumns = [
    {
      title: "Prestation",
      dataIndex: "reference",
      render: (r, p) => (
        <Link
          to={routes.prestations(p._id).detail?.path || "#"}
          target="_blank"
        >
          {r}
        </Link>
      ),
    },
    {
      title: "Commande",
      render: (_, p) => (
        <Space>
          {p.books.name}
          <Tag color={p.books.moves ? "green" : "default"}>
            {p.books.moves ? "migre" : "inchangé"}
          </Tag>
        </Space>
      ),
    },
    {
      title: "Paie",
      render: (_, p) => (
        <Space>
          {p.pays.name}
          <Tag color={p.pays.moves ? "green" : "default"}>
            {p.pays.moves ? "migre" : "inchangé"}
          </Tag>
        </Space>
      ),
    },
  ];

  const collisions = preview.invoiceCollisions;

  const openKeys = [...sections.map(s => s.key), "addr", "xb", "col"];

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {preview.blockers.length > 0 && (
        <Alert
          type="error"
          showIcon
          message="Fusion bloquée"
          description={
            <ul style={{ margin: 0 }}>
              {preview.blockers.map(b => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          }
        />
      )}
      {preview.warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={
            <ul style={{ margin: 0 }}>
              {preview.warnings.map(w => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          }
        />
      )}

      <Alert
        type="info"
        showIcon
        message="Chaque enregistrement concerné est listé ci-dessous avec sa valeur avant et après la fusion. Les totaux sont exactement les chiffres que la vérification contrôlera après la fusion."
      />

      {!mergeId && (
        <Alert
          type="error"
          showIcon
          message="Prévisualisation indisponible"
          description="La fusion n'a pas été transmise à cette étape : rechargez la page ou rouvrez la fusion depuis l'historique."
        />
      )}

      <Collapse defaultActiveKey={openKeys} style={{ width: "100%" }}>
        {mergeId
          ? sections.map(s => (
              <Collapse.Panel key={s.key} header={s.header}>
                <RecordsTable
                  mergeId={mergeId}
                  entry={s}
                  sources={sources}
                  reloadKey={preview.generatedAt}
                />
              </Collapse.Panel>
            ))
          : null}

        <Collapse.Panel
          key="addr"
          header={
            <Space wrap size={[4, 4]}>
              <Text strong>Adresses — propriétaire et usage</Text>
              <Tag>{plural(addr.count, "adresse", "adresses")}</Tag>
              <Tag color="orange">
                {preview.totals.remains.addressesStayingWithOwner} restant à
                leur propriétaire
              </Tag>
            </Space>
          }
        >
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 8 }}
            message="Propriétaire ≠ usage. Une adresse appartenant à une institution hors fusion ne doit JAMAIS changer de propriétaire — seule la mention d'usage est repointée."
          />
          {addr.truncated && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 8 }}
              message={`Cette matrice est limitée aux ${addr.matrix.length} premières adresses sur ${addr.count} — la liste complète est dans les sections « Adresses (propriétaire) » et « Adresses (partagées vers) » ci-dessus.`}
            />
          )}
          <Table
            size="small"
            rowKey="_id"
            columns={addrColumns}
            dataSource={addr.matrix}
            pagination={{ pageSize: 20 }}
          />
        </Collapse.Panel>

        <Collapse.Panel
          key="xb"
          header={
            <Space wrap size={[4, 4]}>
              <Text strong>Facturation croisée</Text>
              <Tag>
                {plural(xbOutside, "prestation", "prestations")} avec
                l'extérieur
              </Tag>
              <Tag>
                {plural(
                  preview.totals.remains.outsidePayers,
                  "payeur externe inchangé",
                  "payeurs externes inchangés",
                )}
              </Tag>
              <Tag>
                {plural(
                  preview.totals.remains.outsideBookers,
                  "commande externe inchangée",
                  "commandes externes inchangées",
                )}
              </Tag>
            </Space>
          }
        >
          <Descriptions
            size="small"
            column={3}
            bordered
            style={{ marginBottom: 8 }}
          >
            <Descriptions.Item label="Fusion commande, extérieur paie">
              {xb.clusterBooksOutsidePays}
            </Descriptions.Item>
            <Descriptions.Item label="Extérieur commande, fusion paie">
              {xb.outsideBooksClusterPays}
            </Descriptions.Item>
            <Descriptions.Item label="Interne à la fusion">
              {xb.internal}
            </Descriptions.Item>
          </Descriptions>
          {xb.items.length ? (
            <Table
              size="small"
              rowKey="_id"
              columns={xbColumns}
              dataSource={xb.items}
              pagination={{ pageSize: 20 }}
            />
          ) : (
            <Text type="secondary">
              Aucune facturation croisée avec l'extérieur — ces prestations
              migrent comme les autres.
            </Text>
          )}
        </Collapse.Panel>

        <Collapse.Panel
          key="col"
          header={
            <Space wrap size={[4, 4]}>
              <Text strong>Collisions de factures</Text>
              <Tag color={collisions.length ? "red" : "green"}>
                {collisions.length}
              </Tag>
            </Space>
          }
        >
          {collisions.length ? (
            <Table
              size="small"
              rowKey={c => `${c.year}-${c.month}-${c.billing}`}
              dataSource={collisions}
              pagination={false}
              columns={[
                {
                  title: "Mois",
                  render: (_, c) =>
                    `${c.year}-${String(c.month).padStart(2, "0")}`,
                },
                {
                  title: "Payeur",
                  dataIndex: "payer",
                  render: p => p || "—",
                },
                {
                  title: "Factures groupées concernées",
                  dataIndex: "bills",
                  render: bs =>
                    bs.map(b => (
                      <Tag key={b._id}>
                        #{b.bill_number} · {b.institution.name} · {b.status}
                        {b.exported ? " · exportée" : ""}
                      </Tag>
                    )),
                },
              ]}
            />
          ) : (
            <Alert
              type="success"
              showIcon
              message="Aucune facture groupée ne se retrouverait en doublon sous une seule institution."
            />
          )}
        </Collapse.Panel>
      </Collapse>
    </Space>
  );
}
